<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import StatusTag from 'dashboard/components-next/tag/StatusTag.vue';
import leadReferralsAPI from 'dashboard/api/leadReferrals';
import AgentRelationshipsAPI from 'dashboard/api/agentRelationships';
import AlertMyReferrals from '../components/AlertMyReferrals.vue';
import {
  REFERRAL_STATUS_META,
  formatDate,
  leadInitials,
} from '../utils/crmPageUtils';
import { trackCrmEvent } from '../utils/crmTracking';

const REFERRALS_PAGE_SIZE = 10;
const SERVICE_LINK_COPY_RESET_MS = 3000;
const SERVICE_LINK_TRACKING_ORIGIN = 'service_link_page';
const OWNER_REFER_COPY_RESET_MS = 3000;
const REFERRAL_DECISION_REASONS = Object.freeze({
  decline: 'agent_unavailable',
  return: 'needs_reassignment',
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');

const referrals = ref([]);
const meta = ref({});
const referralsPage = ref(1);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasError = ref(false);
const statusFilter = ref('');
const showNewReferralDialog = ref(Boolean(route.query.contact_id));
const showConfirmReferralDialog = ref(false);
const showErrorReferralDialog = ref(false);
const showMyReferralDialog = ref(false);
const showServiceLinkDialog = ref(false);
const showOwnerReferDialog = ref(false);
const selectedReferral = ref(null);
const referralErrorReason = ref('');
const inviteSubmitted = ref(false);
const isSubmitting = ref(false);
const isResendingId = ref(null);
const isDecidingId = ref(null);
const isServiceLinkLoading = ref(false);
const serviceLinkCopied = ref(false);
const ownerReferCopied = ref(false);
const serviceLink = ref(null);
const agentProfile = ref({});
const inviteForm = ref({
  name: '',
  phone_number: '',
  contact_id: route.query.contact_id || '',
});

const companyName = computed(
  () =>
    globalConfig.value?.installationName ||
    globalConfig.value?.INSTALLATION_NAME ||
    globalConfig.value?.appName ||
    'Lais'
);

const statusOptions = computed(() => [
  { value: '', label: t('LEADS.REFERRALS.ALL_STATUSES') },
  { value: 'pending', label: t('LEADS.REFERRALS.STATUS.PENDING') },
  { value: 'confirmed', label: t('LEADS.REFERRALS.STATUS.CONFIRMED') },
  { value: 'declined', label: t('LEADS.REFERRALS.STATUS.DECLINED') },
  { value: 'returned', label: t('LEADS.REFERRALS.STATUS.RETURNED') },
  { value: 'expired', label: t('LEADS.REFERRALS.STATUS.EXPIRED') },
  { value: 'inactive', label: t('LEADS.REFERRALS.STATUS.INACTIVE') },
  {
    value: 'not_eligible',
    label: t('LEADS.REFERRALS.STATUS.NOT_ELIGIBLE'),
  },
]);

const howItWorksCards = computed(() => [
  {
    icon: 'i-lucide-send',
    title: t('LEADS.REFERRALS.HOW_INVITE_TITLE'),
    description: t('LEADS.REFERRALS.HOW_INVITE_DESCRIPTION', {
      companyName: companyName.value,
    }),
  },
  {
    icon: 'i-lucide-link',
    title: t('LEADS.REFERRALS.HOW_LINK_TITLE'),
    description: t('LEADS.REFERRALS.HOW_LINK_DESCRIPTION'),
  },
  {
    icon: 'i-lucide-message-circle',
    title: t('LEADS.REFERRALS.HOW_NOTIFY_TITLE'),
    description: t('LEADS.REFERRALS.HOW_NOTIFY_DESCRIPTION'),
  },
]);

const isEmpty = computed(
  () => !isLoading.value && !hasError.value && referrals.value.length === 0
);
const hasInactiveReferral = computed(() =>
  referrals.value.some(referral => referral.status === 'inactive')
);
const hasMoreReferrals = computed(
  () => Number(meta.value?.count || 0) > referrals.value.length
);
const serviceLinkUrl = computed(() => serviceLink.value?.url || '');
const isRedeAgent = computed(() =>
  Boolean(agentProfile.value?.is_rede || agentProfile.value?.isRede)
);
const canShowCommissionNotice = computed(() => !isRedeAgent.value);
const participationTermsUrl = computed(
  () =>
    globalConfig.value?.TERMS_URL ||
    globalConfig.value?.termsUrl ||
    globalConfig.value?.privacyPolicyUrl ||
    '/terms'
);

const serviceLinkHowItWorksCards = computed(() => [
  {
    icon: 'i-lucide-megaphone',
    text: t('LEADS.REFERRALS.SERVICE_LINK_HOW_1', {
      companyName: companyName.value,
    }),
  },
  {
    icon: 'i-lucide-hand-pointer',
    text: t('LEADS.REFERRALS.SERVICE_LINK_HOW_2', {
      companyName: companyName.value,
    }),
  },
  {
    icon: 'i-lucide-user-check',
    text: t('LEADS.REFERRALS.SERVICE_LINK_HOW_3'),
  },
  {
    icon: 'i-lucide-bell-ring',
    text: t('LEADS.REFERRALS.SERVICE_LINK_HOW_4'),
  },
]);

const ownerReferCode = computed(
  () =>
    agentProfile.value?.hash_id ||
    agentProfile.value?.hashId ||
    agentProfile.value?.service_link_code ||
    agentProfile.value?.serviceLinkCode ||
    agentProfile.value?.agent_code ||
    agentProfile.value?.agentCode ||
    accountId.value
);

const ownerReferLink = computed(
  () =>
    `http://quin.to/drcor?codigo=${encodeURIComponent(ownerReferCode.value)}`
);

const ownerReferWhatsappHref = computed(() => {
  const message = t('LEADS.REFERRALS.OWNER_REFER_WHATSAPP_TEXT', {
    companyName: companyName.value,
    link: ownerReferLink.value,
  });
  return `https://api.whatsapp.com/send/?text=${encodeURIComponent(message)}`;
});

const normalizedInviteName = computed(() =>
  inviteForm.value.name.trim().replace(/\s+/g, ' ')
);
const inviteNameHasValidCharacters = computed(() =>
  /^[A-Za-zÀ-ÖØ-öø-ÿ'’ -]+$/u.test(normalizedInviteName.value)
);
const inviteNameParts = computed(() =>
  normalizedInviteName.value.split(/\s+/).filter(Boolean)
);
const isInviteNameValid = computed(
  () =>
    normalizedInviteName.value.length <= 250 &&
    inviteNameParts.value.length >= 2 &&
    inviteNameHasValidCharacters.value
);
const invitePhoneDigits = computed(() =>
  `${inviteForm.value.phone_number || ''}`.replace(/\D/g, '')
);
const isInvitePhoneValid = computed(() => invitePhoneDigits.value.length >= 11);
const showInviteNameError = computed(
  () => inviteSubmitted.value && !isInviteNameValid.value
);
const showInvitePhoneError = computed(
  () => inviteSubmitted.value && !isInvitePhoneValid.value
);
const canCreateInvite = computed(
  () =>
    isInviteNameValid.value && isInvitePhoneValid.value && !isSubmitting.value
);

const loadReferrals = async ({ pageNumber = 1, append = false } = {}) => {
  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }
  hasError.value = false;
  try {
    const { data } = await leadReferralsAPI.get({
      status: statusFilter.value,
      contact_id: route.query.contact_id,
      page: pageNumber,
      per_page: REFERRALS_PAGE_SIZE,
    });
    referrals.value = append
      ? [...referrals.value, ...(data.data || [])]
      : data.data || [];
    meta.value = data.meta || {};
    referralsPage.value = pageNumber;
  } catch (error) {
    hasError.value = true;
    useAlert(error.response?.data?.message || t('LEADS.REFERRALS.ERROR'));
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const loadMoreReferrals = () =>
  loadReferrals({ pageNumber: referralsPage.value + 1, append: true });

const loadAgentProfile = async () => {
  try {
    const { data } = await AgentRelationshipsAPI.me();
    agentProfile.value = data || {};
  } catch {
    agentProfile.value = {};
  }
};

const resetInviteForm = () => {
  inviteForm.value = {
    name: '',
    phone_number: '',
    contact_id: route.query.contact_id || '',
  };
  inviteSubmitted.value = false;
};

const openNewReferralDialog = () => {
  resetInviteForm();
  showNewReferralDialog.value = true;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'TQC_new_referral_clicked',
  });
};

const closeNewReferralDialog = () => {
  if (isSubmitting.value) return;
  showNewReferralDialog.value = false;
};

const submitInvite = async () => {
  inviteSubmitted.value = true;
  if (!canCreateInvite.value) return;

  isSubmitting.value = true;
  try {
    const payload = {
      name: normalizedInviteName.value,
      phone_number: inviteForm.value.phone_number,
      origin: 'referral',
      metadata: {
        source: 'lead_referrals_invite_dialog',
        consent_confirmed: true,
      },
    };
    if (inviteForm.value.contact_id) {
      payload.contact_id = inviteForm.value.contact_id;
    }

    const { data } = await leadReferralsAPI.create(payload);
    if (data.valid === false) {
      selectedReferral.value = data;
      referralErrorReason.value = data.reason || 'generic';
      showErrorReferralDialog.value = true;
      trackCrmEvent({
        accountId: accountId.value,
        name: 'TQC_feedback_pageview',
        properties: { feedback: referralErrorReason.value },
      });
      return;
    }

    useAlert(t('LEADS.REFERRALS.CREATED'));
    selectedReferral.value = data;
    showNewReferralDialog.value = false;
    showConfirmReferralDialog.value = true;
    resetInviteForm();
    trackCrmEvent({
      accountId: accountId.value,
      name: 'TQC_new_referral_send_link_clicked',
      properties: { contact_id: data.contact_id },
    });
    await loadReferrals();
  } catch (error) {
    useAlert(error.response?.data?.errors?.[0] || t('LEADS.REFERRALS.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const isInternalReferralLocator = value =>
  /^lead_referral_locator_/.test(String(value || '')) ||
  /^lead_referral_\d+$/.test(String(value || ''));

const referralPublicToken = referral => {
  const token = referral?.public_token || referral?.code || '';
  return token && !isInternalReferralLocator(token) ? token : '';
};

const referralPublicUrl = referral => {
  const token = referralPublicToken(referral);
  if (!token || typeof window === 'undefined') return '';
  return new URL(`/tqc/${token}`, window.location.origin).toString();
};

const copyLink = async referral => {
  const url = referralPublicUrl(referral);
  if (!url) return;
  await navigator.clipboard.writeText(url);
  useAlert(t('LEADS.REFERRALS.COPIED'));
  trackCrmEvent({
    accountId: accountId.value,
    name: 'TQC_My_referrals_send_link_clicked',
    properties: { referral_id: referral?.id, origin: referral?.origin },
  });
};

const displayReferralName = referral =>
  referral?.contact?.name || `#${referral?.contact_id || ''}`;

const displayReferralPhone = referral =>
  referral?.contact?.phone_number ||
  referral?.phone_number ||
  referral?.metadata?.phone_number ||
  '';

const referralWhatsappHref = computed(() => {
  const url = referralPublicUrl(selectedReferral.value);
  if (!url) return '';
  const message = t('LEADS.REFERRALS.WHATSAPP_TEMPLATE', {
    name: displayReferralName(selectedReferral.value),
    companyName: companyName.value,
    link: url,
  });
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
});

const openMyReferralDialog = () => {
  showMyReferralDialog.value = true;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'TQC_My_referrals_pageview',
  });
};

const openMyServiceLinkDialog = async () => {
  isServiceLinkLoading.value = true;
  try {
    const { data } = await leadReferralsAPI.enableServiceLink();
    serviceLink.value = data;
    serviceLinkCopied.value = false;
    showServiceLinkDialog.value = true;
    trackCrmEvent({
      accountId: accountId.value,
      name: 'service_link_page_viewed',
    });
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        t('LEADS.REFERRALS.SERVICE_LINK_ERROR')
    );
  } finally {
    isServiceLinkLoading.value = false;
  }
};

const openOwnerReferDialog = () => {
  ownerReferCopied.value = false;
  showOwnerReferDialog.value = true;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'owner_referral_dialog_page_viewed',
  });
};

const closeOwnerReferDialog = () => {
  showOwnerReferDialog.value = false;
};

const closeServiceLinkDialog = () => {
  showServiceLinkDialog.value = false;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'service_link_back_button_clicked',
  });
};

const copyServiceLink = async () => {
  if (!serviceLinkUrl.value) return;
  try {
    await navigator.clipboard.writeText(serviceLinkUrl.value);
    serviceLinkCopied.value = true;
    useAlert(t('LEADS.REFERRALS.SERVICE_LINK_COPIED'));
    trackCrmEvent({
      accountId: accountId.value,
      name: 'service_link_copy_link_clicked',
      properties: { origin: SERVICE_LINK_TRACKING_ORIGIN },
    });
    window.setTimeout(() => {
      serviceLinkCopied.value = false;
    }, SERVICE_LINK_COPY_RESET_MS);
  } catch {
    useAlert(t('LEADS.REFERRALS.COPY_ERROR'));
  }
};

const copyOwnerReferLink = async () => {
  try {
    await navigator.clipboard.writeText(ownerReferLink.value);
    ownerReferCopied.value = true;
    useAlert(t('LEADS.REFERRALS.OWNER_REFER_COPY_SUCCESS'));
    trackCrmEvent({
      accountId: accountId.value,
      name: 'owner_referral_copy_link_clicked',
    });
    window.setTimeout(() => {
      ownerReferCopied.value = false;
    }, OWNER_REFER_COPY_RESET_MS);
  } catch {
    useAlert(t('LEADS.REFERRALS.COPY_ERROR'));
  }
};

const openReferralDetails = referral => {
  selectedReferral.value = referral;
  showConfirmReferralDialog.value = true;
};

const openMyReferralDialogFromError = () => {
  showErrorReferralDialog.value = false;
  openMyReferralDialog();
};

const canScheduleStandaloneVisit = referral =>
  isRedeAgent.value && Boolean(referral?.contact_id);

const scheduleStandaloneVisit = referral => {
  if (!canScheduleStandaloneVisit(referral)) return;
  router.push({
    name: 'visita_new',
    params: { accountId: accountId.value },
    query: { contact_id: String(referral.contact_id) },
  });
};

const handleInviteNameKeydown = event => {
  if (
    event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ].includes(event.key)
  ) {
    return;
  }

  if (!/^[A-Za-zÀ-ÖØ-öø-ÿ'’ -]$/u.test(event.key)) {
    event.preventDefault();
  }
};

const openReferralProfile = referral => {
  if (!referral?.contact_id) return;
  router.push({
    name: 'lais_lead_historico',
    params: { accountId: accountId.value, id: referral.contact_id },
  });
};

const canResendReferral = referral =>
  ['pending', 'expired'].includes(referral?.status);

const resendReferral = async referral => {
  if (!referral) return;
  if (referral.status === 'pending') {
    openReferralDetails(referral);
    return;
  }

  isResendingId.value = referral.id;
  try {
    const { data } = await leadReferralsAPI.create({
      contact_id: referral.contact_id,
      origin: 'referral',
      metadata: {
        source: 'lead_referrals_resend',
        resend_of: referral.id,
      },
    });

    if (data.valid === false) {
      referralErrorReason.value = data.reason || 'generic';
      showErrorReferralDialog.value = true;
      return;
    }

    selectedReferral.value = data;
    showConfirmReferralDialog.value = true;
    useAlert(t('LEADS.REFERRALS.RESENT'));
    trackCrmEvent({
      accountId: accountId.value,
      name: 'lead_referral_invite_resent',
      properties: { contact_id: data.contact_id },
    });
    await loadReferrals();
  } catch (error) {
    useAlert(error.response?.data?.errors?.[0] || t('LEADS.REFERRALS.ERROR'));
  } finally {
    isResendingId.value = null;
  }
};

const canDecideReferral = referral => referral?.status === 'pending';
const referralDecision = referral =>
  referral?.metadata?.operational_decision || null;
const referralDecisionActionLabel = action => {
  const labels = {
    accept: t('LEADS.REFERRALS.DECISION_ACCEPTED'),
    decline: t('LEADS.REFERRALS.DECISION_DECLINED'),
    return: t('LEADS.REFERRALS.DECISION_RETURNED'),
  };
  return labels[action] || action;
};
const referralDecisionLine = referral => {
  const decision = referralDecision(referral);
  if (!decision?.action) return '';

  const decisionLabel = referralDecisionActionLabel(decision.action);
  const decisionDate = formatDate(decision.decided_at);
  if (!decisionDate) return decisionLabel;

  return t('LEADS.REFERRALS.DECISION_LINE', {
    decision: decisionLabel,
    date: decisionDate,
  });
};
const isDecidingReferral = (referral, action) =>
  isDecidingId.value === `${referral?.id}:${action}`;
const decideReferral = async (referral, action) => {
  if (!canDecideReferral(referral) || !REFERRAL_DECISION_REASONS[action]) {
    return;
  }

  isDecidingId.value = `${referral.id}:${action}`;
  try {
    const { data } = await leadReferralsAPI.decide(referral.id, {
      action,
      reason: REFERRAL_DECISION_REASONS[action],
      metadata: { source: 'lead_referrals_index' },
    });
    selectedReferral.value = data;
    useAlert(t('LEADS.REFERRALS.DECISION_SAVED'));
    trackCrmEvent({
      accountId: accountId.value,
      name: 'lead_referral_decision_saved',
      properties: { referral_id: referral.id, action },
    });
    await loadReferrals();
  } catch (error) {
    useAlert(error.response?.data?.errors?.[0] || t('LEADS.REFERRALS.ERROR'));
  } finally {
    isDecidingId.value = null;
  }
};

const statusMeta = status =>
  REFERRAL_STATUS_META[status] || REFERRAL_STATUS_META.invalid;
const statusLabel = status => {
  const labels = {
    pending: t('LEADS.REFERRALS.STATUS.PENDING'),
    confirmed: t('LEADS.REFERRALS.STATUS.CONFIRMED'),
    declined: t('LEADS.REFERRALS.STATUS.DECLINED'),
    returned: t('LEADS.REFERRALS.STATUS.RETURNED'),
    expired: t('LEADS.REFERRALS.STATUS.EXPIRED'),
    inactive: t('LEADS.REFERRALS.STATUS.INACTIVE'),
    not_eligible: t('LEADS.REFERRALS.STATUS.NOT_ELIGIBLE'),
    invalid: t('LEADS.REFERRALS.STATUS.INVALID'),
  };
  return labels[status] || labels.invalid;
};
const referralOriginLabel = referral =>
  referral?.origin === 'service_link'
    ? t('LEADS.REFERRALS.ORIGIN_SERVICE_LINK')
    : t('LEADS.REFERRALS.ORIGIN_INVITE');
const referralOriginIcon = referral =>
  referral?.origin === 'service_link' ? 'i-lucide-anchor' : 'i-lucide-mail';
const referralMetaLine = referral =>
  [referralOriginLabel(referral), formatDate(referral.created_at)]
    .filter(Boolean)
    .join(' · ');

const referralErrorMessage = computed(() => {
  if (referralErrorReason.value === 'already_referred') {
    return t('LEADS.REFERRALS.ERRORS.ALREADY_REFERRED', {
      companyName: companyName.value,
    });
  }
  if (referralErrorReason.value === 'already_active') {
    return t('LEADS.REFERRALS.ERRORS.ALREADY_ACTIVE', {
      companyName: companyName.value,
    });
  }
  if (referralErrorReason.value === 'accepted_by_other') {
    return t('LEADS.REFERRALS.ERRORS.ACCEPTED_BY_OTHER', {
      companyName: companyName.value,
    });
  }
  if (referralErrorReason.value === 'recent_activity') {
    return t('LEADS.REFERRALS.ERRORS.RECENT_ACTIVITY', {
      companyName: companyName.value,
    });
  }

  return t('LEADS.REFERRALS.ERRORS.GENERIC', {
    companyName: companyName.value,
  });
});

onMounted(() => {
  loadReferrals();
  loadAgentProfile();
});
</script>

<template>
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      :title="t('LEADS.REFERRALS.TITLE')"
      :helper="t('LEADS.REFERRALS.HELPER')"
    />

    <div
      class="grid gap-5 border-b border-lais-border-soft px-4 py-5 sm:px-6 lg:px-10"
    >
      <LaisCard density="tight">
        <div class="grid items-center gap-5 lg:grid-cols-[1fr_auto]">
          <div class="grid gap-3">
            <p
              class="font-redHatDisplay text-lais-heading-lg font-semibold text-lais-text"
            >
              {{
                t('LEADS.REFERRALS.HERO_TITLE', {
                  companyName,
                })
              }}
            </p>
            <p
              class="max-w-3xl text-lais-body-sm leading-relaxed text-lais-text-muted"
            >
              {{
                t('LEADS.REFERRALS.HERO_DESCRIPTION', {
                  companyName,
                  brandName: companyName,
                })
              }}
            </p>
            <div class="flex flex-wrap gap-3">
              <LaisPrimaryButton
                icon="i-lucide-user-plus"
                @click="openNewReferralDialog"
              >
                {{ t('LEADS.REFERRALS.INVITE_CLIENT') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-list"
                @click="openMyReferralDialog"
              >
                {{ t('LEADS.REFERRALS.MY_INVITES') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-link-2"
                :is-loading="isServiceLinkLoading"
                @click="openMyServiceLinkDialog"
              >
                {{ t('LEADS.REFERRALS.MY_SERVICE_LINK') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-share-2"
                @click="openOwnerReferDialog"
              >
                {{ t('LEADS.REFERRALS.OWNER_REFER_BUTTON') }}
              </LaisPrimaryButton>
            </div>
          </div>
          <div
            class="hidden min-w-[180px] rounded-lais-xl border border-lais-primary/20 bg-lais-primary-50 p-4 text-lais-primary lg:grid"
            aria-hidden="true"
          >
            <div class="grid justify-items-center gap-3 text-center">
              <span
                class="grid size-16 place-items-center rounded-full bg-white shadow-lais-card"
              >
                <span class="i-lucide-handshake size-8" />
              </span>
              <span class="i-lucide-badge-dollar-sign size-7" />
            </div>
          </div>
        </div>
      </LaisCard>

      <LaisCard density="tight">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
          <span
            class="grid size-12 shrink-0 place-items-center rounded-full bg-lais-success/10 text-lais-success"
            aria-hidden="true"
          >
            <span class="i-lucide-circle-dollar-sign size-6" />
          </span>
          <div class="min-w-0 flex-1">
            <h2
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              {{ t('LEADS.REFERRALS.BUDGET_TITLE') }}
            </h2>
            <p class="mt-1 text-lais-body-sm text-lais-text-muted">
              {{ t('LEADS.REFERRALS.BUDGET_DESCRIPTION') }}
            </p>
          </div>
          <a
            :href="participationTermsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-9 items-center justify-center rounded-lais border border-lais-border px-3 text-lais-label-sm font-semibold text-lais-primary hover:border-lais-primary"
          >
            {{ t('LEADS.REFERRALS.TERMS_LINK') }}
          </a>
        </div>
      </LaisCard>

      <section class="grid gap-3">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2
            class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
          >
            {{ t('LEADS.REFERRALS.HOW_IT_WORKS') }}
          </h2>
          <label class="grid min-w-[220px] gap-1">
            <span
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('LEADS.REFERRALS.STATUS_FILTER') }}
            </span>
            <select
              v-model="statusFilter"
              class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm"
              @change="loadReferrals()"
            >
              <option
                v-for="option in statusOptions"
                :key="option.value || 'all'"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <div class="grid gap-3 md:grid-cols-3">
          <LaisCard
            v-for="card in howItWorksCards"
            :key="card.title"
            density="tight"
          >
            <div class="grid gap-3">
              <span
                class="grid size-10 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
                :class="card.icon"
                aria-hidden="true"
              />
              <div>
                <h3 class="font-semibold text-lais-text">
                  {{ card.title }}
                </h3>
                <p
                  class="mt-1 text-lais-body-sm leading-relaxed text-lais-text-muted"
                >
                  {{ card.description }}
                </p>
              </div>
            </div>
          </LaisCard>
        </div>
      </section>
    </div>

    <div
      class="min-h-0 flex-1 overflow-auto px-4 py-5 sm:px-6 lg:px-10 max-md:flex-none max-md:overflow-visible"
    >
      <LaisCard v-if="hasError" density="tight">
        <button
          type="button"
          class="font-semibold text-lais-primary"
          @click="loadReferrals"
        >
          {{ t('LEADS.PIPELINE.RETRY') }}
        </button>
      </LaisCard>
      <LaisCard v-else-if="isEmpty" density="tight">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-lais-body-sm text-lais-text-muted">
            {{ t('LEADS.REFERRALS.EMPTY_HOME') }}
          </p>
          <LaisPrimaryButton
            size="sm"
            icon="i-lucide-user-plus"
            @click="openNewReferralDialog"
          >
            {{ t('LEADS.REFERRALS.INVITE_CLIENT') }}
          </LaisPrimaryButton>
        </div>
      </LaisCard>
      <div v-else class="grid gap-3">
        <LaisCard
          v-for="referral in referrals"
          :key="referral.id"
          density="tight"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex min-w-0 items-center gap-3">
              <span
                class="grid size-10 shrink-0 place-items-center rounded-full bg-lais-primary-50 font-redHatDisplay text-lais-label-sm font-semibold text-lais-primary"
              >
                <span :class="referralOriginIcon(referral)" class="size-4" />
              </span>
              <div class="min-w-0">
                <p class="truncate font-semibold text-lais-text">
                  {{ displayReferralName(referral) }}
                </p>
                <p class="truncate text-lais-body-xs text-lais-text-muted">
                  {{
                    displayReferralPhone(referral) || referralMetaLine(referral)
                  }}
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <StatusTag
                :label="statusLabel(referral.status)"
                :variant="statusMeta(referral.status).variant"
              />
              <LaisPrimaryButton
                v-if="referralPublicToken(referral)"
                size="sm"
                variant="ghost"
                icon="i-lucide-copy"
                @click="copyLink(referral)"
              >
                {{ t('LEADS.REFERRALS.COPY_LINK') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canResendReferral(referral)"
                size="sm"
                variant="ghost"
                icon="i-lucide-send"
                :is-loading="isResendingId === referral.id"
                @click="resendReferral(referral)"
              >
                {{ t('LEADS.REFERRALS.RESEND_INVITE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canDecideReferral(referral)"
                size="sm"
                variant="ghost"
                icon="i-lucide-circle-x"
                :is-loading="isDecidingReferral(referral, 'decline')"
                @click="decideReferral(referral, 'decline')"
              >
                {{ t('LEADS.REFERRALS.DECLINE_INVITE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canDecideReferral(referral)"
                size="sm"
                variant="ghost"
                icon="i-lucide-undo-2"
                :is-loading="isDecidingReferral(referral, 'return')"
                @click="decideReferral(referral, 'return')"
              >
                {{ t('LEADS.REFERRALS.RETURN_INVITE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="referral.status === 'confirmed'"
                size="sm"
                variant="ghost"
                icon="i-lucide-user-round"
                @click="openReferralProfile(referral)"
              >
                {{ t('LEADS.REFERRALS.VIEW_PROFILE') }}
              </LaisPrimaryButton>
              <button
                type="button"
                class="grid size-9 place-items-center rounded-full border border-lais-border-soft text-lais-text-muted hover:border-lais-primary hover:text-lais-primary"
                :aria-label="t('LEADS.REFERRALS.VIEW_INVITE')"
                @click="openReferralDetails(referral)"
              >
                <span class="i-lucide-eye size-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          <p class="mt-2 text-lais-body-xs text-lais-text-muted">
            {{ referralMetaLine(referral) }}
          </p>
          <p
            v-if="referralDecisionLine(referral)"
            class="mt-1 text-lais-body-xs font-semibold text-lais-text-body"
          >
            {{ referralDecisionLine(referral) }}
          </p>
        </LaisCard>
        <div v-if="hasMoreReferrals" class="flex justify-center pt-2">
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-chevron-down"
            :is-loading="isLoadingMore"
            @click="loadMoreReferrals"
          >
            {{ t('LEADS.REFERRALS.LOAD_MORE') }}
          </LaisPrimaryButton>
        </div>
      </div>
    </div>

    <LaisModal
      :show="showNewReferralDialog"
      :title="t('LEADS.REFERRALS.NEW_DIALOG_TITLE')"
      max-width-class="max-w-[560px]"
      @close="closeNewReferralDialog"
    >
      <form class="grid gap-4" @submit.prevent="submitInvite">
        <p class="text-lais-body-sm leading-relaxed text-lais-text-muted">
          {{ t('LEADS.REFERRALS.NEW_DIALOG_HELPER') }}
        </p>

        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('LEADS.REFERRALS.NAME_LABEL') }}
          </span>
          <input
            v-model="inviteForm.name"
            type="text"
            maxlength="250"
            class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm text-lais-text"
            :placeholder="t('LEADS.REFERRALS.NAME_PLACEHOLDER')"
            :aria-invalid="showInviteNameError"
            aria-describedby="lead-referral-name-error"
            @keydown="handleInviteNameKeydown"
          />
          <p
            v-if="showInviteNameError"
            id="lead-referral-name-error"
            class="text-lais-body-xs font-semibold text-red-700"
          >
            {{ t('LEADS.REFERRALS.NAME_ERROR') }}
          </p>
        </label>

        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('LEADS.REFERRALS.PHONE_LABEL') }}
          </span>
          <PhoneNumberInput
            v-model="inviteForm.phone_number"
            :placeholder="t('LEADS.REFERRALS.PHONE_PLACEHOLDER')"
            show-border
          />
          <p
            v-if="showInvitePhoneError"
            class="text-lais-body-xs font-semibold text-red-700"
          >
            {{ t('LEADS.REFERRALS.PHONE_ERROR') }}
          </p>
        </label>

        <div
          class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-lais-body-xs leading-relaxed text-lais-text-muted"
        >
          {{
            t('LEADS.REFERRALS.CONSENT', {
              companyName,
            })
          }}
        </div>
      </form>
      <template #footer>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton
            variant="ghost"
            :disabled="isSubmitting"
            @click="closeNewReferralDialog"
          >
            {{ t('LEADS.REFERRALS.CANCEL') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            :disabled="!canCreateInvite"
            :is-loading="isSubmitting"
            @click="submitInvite"
          >
            {{ t('LEADS.REFERRALS.CREATE_INVITE_LINK') }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="showConfirmReferralDialog"
      :title="t('LEADS.REFERRALS.READY_TITLE')"
      max-width-class="max-w-[560px]"
      @close="showConfirmReferralDialog = false"
    >
      <div v-if="selectedReferral" class="grid gap-4">
        <div
          class="flex items-center gap-3 rounded-lais border border-lais-border bg-lais-bg-soft p-4"
        >
          <span
            class="grid size-12 shrink-0 place-items-center rounded-full bg-lais-primary-50 font-redHatDisplay text-lais-label-md font-semibold text-lais-primary"
          >
            {{ leadInitials({ name: displayReferralName(selectedReferral) }) }}
          </span>
          <div class="min-w-0">
            <p class="truncate font-semibold text-lais-text">
              {{ displayReferralName(selectedReferral) }}
            </p>
            <p class="truncate text-lais-body-xs text-lais-text-muted">
              {{ displayReferralPhone(selectedReferral) }}
            </p>
          </div>
        </div>

        <p class="text-lais-body-sm leading-relaxed text-lais-text-muted">
          {{ t('LEADS.REFERRALS.READY_HELPER') }}
        </p>
        <div
          v-if="canShowCommissionNotice"
          class="rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-lais-body-xs font-semibold leading-relaxed text-amber-900"
        >
          {{ t('LEADS.REFERRALS.COMMISSION_NOTICE') }}
        </div>
        <div
          v-if="selectedReferral.status === 'confirmed'"
          class="rounded-lais-sm border border-lais-primary-200 bg-lais-primary-50 px-3 py-2 text-lais-body-xs font-semibold text-lais-primary"
        >
          {{ t('LEADS.REFERRALS.ALREADY_CONFIRMED') }}
        </div>
        <p class="break-all text-lais-body-sm text-lais-text-body">
          {{ referralPublicUrl(selectedReferral) }}
        </p>
        <p
          v-if="canScheduleStandaloneVisit(selectedReferral)"
          class="text-lais-body-xs leading-relaxed text-lais-text-muted"
        >
          {{ t('LEADS.REFERRALS.STANDALONE_VISIT_HELPER') }}
        </p>
      </div>
      <template #footer>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton
            v-if="canScheduleStandaloneVisit(selectedReferral)"
            variant="ghost"
            icon="i-lucide-calendar-plus"
            @click="scheduleStandaloneVisit(selectedReferral)"
          >
            {{ t('LEADS.REFERRALS.STANDALONE_VISIT') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="selectedReferral"
            variant="ghost"
            icon="i-lucide-copy"
            @click="copyLink(selectedReferral)"
          >
            {{ t('LEADS.REFERRALS.COPY_LINK') }}
          </LaisPrimaryButton>
          <a
            v-if="referralWhatsappHref"
            :href="referralWhatsappHref"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 py-2.5 text-lais-label-sm font-semibold text-white"
          >
            <span class="i-lucide-message-circle size-4" aria-hidden="true" />
            {{ t('LEADS.REFERRALS.SEND_WHATSAPP') }}
          </a>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="showErrorReferralDialog"
      :title="t('LEADS.REFERRALS.ERROR_TITLE')"
      max-width-class="max-w-[480px]"
      @close="showErrorReferralDialog = false"
    >
      <div class="grid gap-4">
        <div
          class="mx-auto grid size-12 place-items-center rounded-full bg-red-50 text-red-700"
        >
          <span class="i-lucide-triangle-alert size-5" aria-hidden="true" />
        </div>
        <p
          class="text-center text-lais-body-sm leading-relaxed text-lais-text-body"
        >
          {{ referralErrorMessage }}
        </p>
        <p
          class="text-center text-lais-body-xs leading-relaxed text-lais-text-muted"
        >
          {{ t('LEADS.REFERRALS.TERMS_HELPER') }}
          <a
            :href="participationTermsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="font-semibold text-lais-primary underline"
          >
            {{ t('LEADS.REFERRALS.TERMS_LINK') }}
          </a>
        </p>
      </div>
      <template #footer>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton
            v-if="canScheduleStandaloneVisit(selectedReferral)"
            variant="ghost"
            icon="i-lucide-calendar-plus"
            @click="scheduleStandaloneVisit(selectedReferral)"
          >
            {{ t('LEADS.REFERRALS.STANDALONE_VISIT') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            @click="openMyReferralDialogFromError"
          >
            {{ t('LEADS.REFERRALS.MY_INVITES') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton @click="showErrorReferralDialog = false">
            {{ t('LEADS.REFERRALS.OK') }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="showServiceLinkDialog"
      :title="t('LEADS.REFERRALS.SERVICE_LINK_TITLE')"
      max-width-class="max-w-[620px]"
      full-screen
      data-testid="my-service-link-dialog-test-id"
      @close="closeServiceLinkDialog"
    >
      <template #header>
        <div class="flex min-w-0 items-center gap-3">
          <button
            type="button"
            class="grid size-9 shrink-0 place-items-center rounded-full text-lais-text-muted hover:bg-lais-primary-50 hover:text-lais-primary"
            :aria-label="t('GENERAL_SETTINGS.BACK')"
            @click="closeServiceLinkDialog"
          >
            <span class="i-lucide-arrow-left size-4" aria-hidden="true" />
          </button>
          <h2
            class="font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
          >
            {{ t('LEADS.REFERRALS.SERVICE_LINK_TITLE') }}
          </h2>
        </div>
      </template>
      <div class="grid gap-5">
        <p class="text-lais-body-sm leading-relaxed text-lais-text-muted">
          {{ t('LEADS.REFERRALS.SERVICE_LINK_ABOUT') }}
        </p>

        <div
          class="grid gap-3 rounded-lais border border-lais-border bg-lais-bg-soft p-4"
        >
          <div
            class="grid place-items-center gap-2 rounded-lais-sm border border-lais-primary-100 bg-lais-bg-card px-3 py-4 text-center"
          >
            <span
              class="i-lucide-link-2 size-6 text-lais-primary"
              aria-hidden="true"
            />
            <p class="break-all text-lais-body-sm font-semibold text-lais-text">
              {{ serviceLinkUrl }}
            </p>
          </div>
          <LaisPrimaryButton
            icon="i-lucide-copy"
            :variant="serviceLinkCopied ? 'ghost' : 'primary'"
            :disabled="serviceLinkCopied"
            @click="copyServiceLink"
          >
            {{
              serviceLinkCopied
                ? t('LEADS.REFERRALS.SERVICE_LINK_COPY_SUCCESS')
                : t('LEADS.REFERRALS.SERVICE_LINK_COPY')
            }}
          </LaisPrimaryButton>
          <p class="text-lais-body-xs leading-relaxed text-lais-text-muted">
            {{ t('LEADS.REFERRALS.SERVICE_LINK_INFO') }}
          </p>
        </div>

        <section class="grid gap-3 rounded-lais bg-lais-primary-50 p-4">
          <h3
            class="font-redHatDisplay text-lais-heading-xs font-semibold text-lais-text"
          >
            {{ t('LEADS.REFERRALS.SERVICE_LINK_SUBTITLE') }}
          </h3>
          <div class="grid gap-3">
            <div
              v-for="item in serviceLinkHowItWorksCards"
              :key="item.text"
              class="grid grid-cols-[auto_1fr] gap-3"
            >
              <span
                class="mt-0.5 size-5 text-lais-primary"
                :class="item.icon"
                aria-hidden="true"
              />
              <p class="text-lais-body-sm leading-relaxed text-lais-text-body">
                {{ item.text }}
              </p>
            </div>
          </div>
        </section>
      </div>
    </LaisModal>

    <LaisModal
      :show="showOwnerReferDialog"
      :title="t('LEADS.REFERRALS.OWNER_REFER_TITLE')"
      max-width-class="max-w-[520px]"
      data-testid="refer_dialog_test_id"
      @close="closeOwnerReferDialog"
    >
      <div class="grid gap-4">
        <div
          class="mx-auto grid size-14 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
          aria-hidden="true"
        >
          <span class="i-lucide-share-2 size-7" />
        </div>
        <p
          class="text-center text-lais-body-sm leading-relaxed text-lais-text-body"
        >
          {{ t('LEADS.REFERRALS.OWNER_REFER_DESCRIPTION') }}
        </p>
        <div
          class="grid gap-2 rounded-lais border border-lais-border bg-lais-bg-soft p-4"
        >
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('LEADS.REFERRALS.OWNER_REFER_LINK_LABEL') }}
          </span>
          <p class="break-all text-lais-body-sm font-semibold text-lais-text">
            {{ ownerReferLink }}
          </p>
        </div>
      </div>
      <template #footer>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton
            icon="i-lucide-copy"
            :variant="ownerReferCopied ? 'ghost' : 'primary'"
            :disabled="ownerReferCopied"
            @click="copyOwnerReferLink"
          >
            {{
              ownerReferCopied
                ? t('LEADS.REFERRALS.OWNER_REFER_COPY_SUCCESS')
                : t('LEADS.REFERRALS.OWNER_REFER_COPY')
            }}
          </LaisPrimaryButton>
          <a
            :href="ownerReferWhatsappHref"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 py-2.5 text-lais-label-sm font-semibold text-white"
          >
            <span class="i-lucide-message-circle size-4" aria-hidden="true" />
            {{ t('LEADS.REFERRALS.OWNER_REFER_SHARE') }}
          </a>
        </div>
      </template>
    </LaisModal>

    <LaisDrawer
      :show="showMyReferralDialog"
      :title="t('LEADS.REFERRALS.MY_INVITES')"
      side="right"
      size="md"
      @close="showMyReferralDialog = false"
    >
      <template #body>
        <div class="grid gap-3">
          <AlertMyReferrals
            v-if="hasInactiveReferral"
            :message="t('LEADS.REFERRALS.INACTIVE_ALERT')"
          />
          <div
            v-if="isEmpty"
            class="grid justify-items-center gap-3 rounded-lais border border-dashed border-lais-border-soft px-4 py-8 text-center text-lais-body-sm text-lais-text-muted"
          >
            <span
              class="grid size-12 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
            >
              <span class="i-lucide-hourglass size-6" aria-hidden="true" />
            </span>
            <span>{{ t('LEADS.REFERRALS.EMPTY_DRAWER') }}</span>
          </div>
          <template v-else>
            <article
              v-for="referral in referrals"
              :key="`drawer-${referral.id}`"
              class="rounded-lais border border-lais-border p-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate font-semibold text-lais-text">
                    {{ displayReferralName(referral) }}
                  </p>
                  <p
                    class="mt-1 truncate text-lais-body-xs text-lais-text-muted"
                  >
                    {{
                      displayReferralPhone(referral) ||
                      referralMetaLine(referral)
                    }}
                  </p>
                </div>
                <StatusTag
                  :label="statusLabel(referral.status)"
                  :variant="statusMeta(referral.status).variant"
                />
              </div>
              <p class="mt-2 text-lais-body-xs text-lais-text-muted">
                {{ referralMetaLine(referral) }}
              </p>
              <div class="mt-3 flex flex-wrap gap-2">
                <LaisPrimaryButton
                  v-if="referralPublicToken(referral)"
                  size="sm"
                  variant="ghost"
                  icon="i-lucide-copy"
                  @click="copyLink(referral)"
                >
                  {{ t('LEADS.REFERRALS.COPY_LINK') }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="canResendReferral(referral)"
                  size="sm"
                  variant="ghost"
                  icon="i-lucide-send"
                  :is-loading="isResendingId === referral.id"
                  @click="resendReferral(referral)"
                >
                  {{ t('LEADS.REFERRALS.RESEND_INVITE') }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="canDecideReferral(referral)"
                  size="sm"
                  variant="ghost"
                  icon="i-lucide-circle-x"
                  :is-loading="isDecidingReferral(referral, 'decline')"
                  @click="decideReferral(referral, 'decline')"
                >
                  {{ t('LEADS.REFERRALS.DECLINE_INVITE') }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="canDecideReferral(referral)"
                  size="sm"
                  variant="ghost"
                  icon="i-lucide-undo-2"
                  :is-loading="isDecidingReferral(referral, 'return')"
                  @click="decideReferral(referral, 'return')"
                >
                  {{ t('LEADS.REFERRALS.RETURN_INVITE') }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="referral.status === 'confirmed'"
                  size="sm"
                  variant="ghost"
                  icon="i-lucide-user-round"
                  @click="openReferralProfile(referral)"
                >
                  {{ t('LEADS.REFERRALS.VIEW_PROFILE') }}
                </LaisPrimaryButton>
              </div>
              <p
                v-if="referralDecisionLine(referral)"
                class="mt-2 text-lais-body-xs font-semibold text-lais-text-body"
              >
                {{ referralDecisionLine(referral) }}
              </p>
            </article>
          </template>
          <div v-if="hasMoreReferrals" class="flex justify-center pt-1">
            <LaisPrimaryButton
              size="sm"
              variant="ghost"
              icon="i-lucide-chevron-down"
              :is-loading="isLoadingMore"
              @click="loadMoreReferrals"
            >
              {{ t('LEADS.REFERRALS.LOAD_MORE') }}
            </LaisPrimaryButton>
          </div>
        </div>
      </template>
    </LaisDrawer>
  </section>
</template>
